import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import { listDeployJobs } from "../../../services/writingDeployApi";

const STATUS_CONFIG = {
	discarded: {
		key: "discarded",
		label: "Discarded",
		icon: "fa-trash-alt",
	},
	failed: {
		key: "failed",
		label: "Failed patch",
		icon: "fa-exclamation-triangle",
	},
	error: {
		key: "failed",
		label: "Failed patch",
		icon: "fa-exclamation-triangle",
	},
	overwritten: {
		key: "overwritten",
		label: "Completed",
		icon: "fa-sync-alt",
	},
	reverted: {
		key: "reverted",
		label: "Reverted",
		icon: "fa-undo",
	},
	awaiting_review: {
		key: "awaiting_review",
		label: "Awaiting Review",
		icon: "fa-clock",
	},
};

const SORTABLE_COLUMNS = [
	{ key: "id", label: "Job ID" },
	{ key: "page", label: "Page & step" },
	{ key: "started", label: "Started" },
	{ key: "status", label: "Status" },
];

function isFailedDeploy(status) {
	const s = String(status || "").toLowerCase();
	return s === "failed" || s === "error";
}

function deployDetailPath(jobId) {
	return `/aivisibility/deployments/view?jobId=${encodeURIComponent(jobId)}`;
}

function getStatusConfig(status) {
	const s = String(status || "").toLowerCase();
	if (STATUS_CONFIG[s]) return STATUS_CONFIG[s];
	const label = s
		? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
		: "Unknown";
	return { key: "other", label, icon: "fa-circle" };
}

function truncateUrl(url, maxLen = 36) {
	const raw = String(url || "").trim();
	if (!raw) return "—";
	let display = raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
	if (display.length <= maxLen) return display;
	const keepStart = Math.max(10, Math.floor(maxLen * 0.55));
	const keepEnd = Math.max(8, maxLen - keepStart - 3);
	return `${display.slice(0, keepStart)}...${display.slice(-keepEnd)}`;
}

function formatStartedParts(dateString) {
	if (!dateString) return { date: "—", time: "" };
	try {
		const d = new Date(dateString);
		if (Number.isNaN(d.getTime())) return { date: "—", time: "" };
		return {
			date: format(d, "d MMM yyyy"),
			time: format(d, "h:mm a"),
		};
	} catch {
		return { date: "—", time: "" };
	}
}

function buildStepLine(job) {
	const parts = [];
	if (job.currentStep) parts.push(String(job.currentStep));
	if (job.writingJobId != null) parts.push(`Writing job #${job.writingJobId}`);
	return parts.join(" · ");
}

function sortValue(job, key) {
	switch (key) {
		case "id": {
			const n = Number(job.id);
			return Number.isFinite(n) ? n : String(job.id || "");
		}
		case "page":
			return String(job.sourceUrl || "").toLowerCase();
		case "started": {
			const raw = job.startedAt || job.createdAt;
			if (!raw) return 0;
			const t = new Date(raw).getTime();
			return Number.isNaN(t) ? 0 : t;
		}
		case "status":
			return String(job.status || "").toLowerCase();
		default:
			return "";
	}
}

function compareJobs(a, b, key, dir) {
	const mul = dir === "asc" ? 1 : -1;
	const av = sortValue(a, key);
	const bv = sortValue(b, key);
	if (typeof av === "number" && typeof bv === "number") {
		return (av - bv) * mul;
	}
	return String(av).localeCompare(String(bv), undefined, {
		numeric: true,
		sensitivity: "base",
	}) * mul;
}

function SortableTh({ columnKey, label, sort, onSort }) {
	const active = sort.key === columnKey;
	const ariaSort = active
		? sort.dir === "asc"
			? "ascending"
			: "descending"
		: "none";
	const icon = !active
		? "fa-sort"
		: sort.dir === "asc"
			? "fa-sort-up"
			: "fa-sort-down";

	return (
		<th scope="col" aria-sort={ariaSort}>
			<button
				type="button"
				className={`writing-deploy-list__sort-btn${active ? " is-active" : ""}`}
				onClick={() => onSort(columnKey)}
			>
				{label}
				<i className={`fas ${icon}`} aria-hidden />
			</button>
		</th>
	);
}

const AiVisibilityWritingDeployments = () => {
	const navigate = useNavigate();
	const { currentWebsiteId, websitePlatform, userLoader, websiteLoader } =
		useContext(ProductContext);
	const [jobs, setJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [sort, setSort] = useState({ key: "started", dir: "desc" });

	const fetchJobs = useCallback(async () => {
		if (!currentWebsiteId) {
			setJobs([]);
			setLoading(false);
			return;
		}
		setLoading(true);
		const result = await listDeployJobs(currentWebsiteId, websitePlatform);
		if (!result.ok) {
			toast.error(result.message || "Failed to load deployments");
			setJobs([]);
		} else {
			setJobs(result.jobs || []);
		}
		setLoading(false);
	}, [currentWebsiteId, websitePlatform]);

	useEffect(() => {
		if (userLoader || websiteLoader) return;
		fetchJobs();
	}, [userLoader, websiteLoader, fetchJobs]);

	const handleSort = (key) => {
		setSort((prev) => {
			if (prev.key === key) {
				return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
			}
			return { key, dir: key === "started" || key === "id" ? "desc" : "asc" };
		});
	};

	const sortedJobs = useMemo(() => {
		const list = [...jobs];
		list.sort((a, b) => compareJobs(a, b, sort.key, sort.dir));
		return list;
	}, [jobs, sort]);

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	if (!currentWebsiteId) {
		return null;
	}

	return (
		<div className="contentBlock ai-visibility-dashboard writing-assistance writing-deploy-list">
			<div className="pageHeader mb-3">
				<h2 className="pageTitle mb-1">Deployments</h2>
				{/* <p className="text-muted mb-0">
					WordPress deploy jobs from Content Studio writing previews.
				</p> */}
			</div>

			<Card className="shadow-sm writing-assistance__your-jobs">
				<CardBody className="p-0">
					{loading ? (
						<div className="text-center py-4">
							<Spinner size="sm" />
						</div>
					) : jobs.length === 0 ? (
						<p className="text-muted text-center py-4 mb-0 px-3">
							No deployments yet. Open a writing preview with auto-deploy enabled
							and deploy a change.
						</p>
					) : (
						<div className="writing-deploy-list__table-wrap">
							<table className="writing-deploy-list__table">
								<thead>
									<tr>
										{SORTABLE_COLUMNS.map((col) => (
											<SortableTh
												key={col.key}
												columnKey={col.key}
												label={col.label}
												sort={sort}
												onSort={handleSort}
											/>
										))}
										<th scope="col">Action</th>
									</tr>
								</thead>
								<tbody>
									{sortedJobs.map((job) => {
										const failed = isFailedDeploy(job.status);
										const statusCfg = getStatusConfig(job.status);
										const started = formatStartedParts(
											job.startedAt || job.createdAt
										);
										const stepLine = buildStepLine(job);
										const detailPath = deployDetailPath(job.id);
										const actionLabel =
											String(job.status).toLowerCase() === "awaiting_review"
												? "Review"
												: "View";

										return (
											<tr key={job.id}>
												<td className="writing-deploy-list__id">
													{failed ? (
														<span className="writing-deploy-list__id-plain">
															#{job.id}
														</span>
													) : (
														<button
															type="button"
															className="writing-deploy-list__id-link"
															onClick={() => navigate(detailPath)}
														>
															#{job.id}
														</button>
													)}
												</td>
												<td className="writing-deploy-list__page">
													<div
														className="writing-deploy-list__page-url"
														title={job.sourceUrl || undefined}
													>
														{truncateUrl(job.sourceUrl)}
													</div>
													{stepLine ? (
														<div className="writing-deploy-list__page-step">
															{stepLine}
														</div>
													) : null}
												</td>
												<td className="writing-deploy-list__started">
													<div className="writing-deploy-list__started-date">
														{started.date}
													</div>
													{started.time ? (
														<div className="writing-deploy-list__started-time">
															{started.time}
														</div>
													) : null}
												</td>
												<td>
													<span
														className={`writing-deploy-list__status writing-deploy-list__status--${statusCfg.key}`}
													>
														<i className={`fas ${statusCfg.icon}`} aria-hidden />
														{statusCfg.label}
													</span>
													{String(job.status).toLowerCase() ===
														"awaiting_review" && job.isRevert ? (
														<div className="writing-deploy-list__page-step mt-1">
															Revert preview
														</div>
													) : null}
												</td>
												<td className="writing-deploy-list__action">
													<Button
														color="light"
														size="sm"
														className="writing-deploy-list__view-btn border"
														disabled={failed}
														onClick={() => navigate(detailPath)}
													>
														{actionLabel}
													</Button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</CardBody>
			</Card>
		</div>
	);
};

export default AiVisibilityWritingDeployments;
